import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Inbox } from "lucide-react";
import AutosTag, { autosLabel } from "@/components/AutosTag";
import { formatDate } from "@/lib/format";

const ORIGEM = { datajud: "DataJud", djen: "DJEN", push: "PUSH", colado: "Colado", manual: "Manual" };

// Lote 118 — fila "A classificar": andamentos capturados sozinhos (DJEN/DataJud) de um número com vários autos,
// quando o sistema não sabe de qual são. 1 toque abre a fila; 1 toque na etiqueta classifica (Desfazer por 8 s).
// O aviso é SEMPRE visível (não recolhe) enquanto houver itens.
export default function FilaClassificar({ processoId, total, onChanged }) {
  const [open, setOpen] = useState(false);
  const [itens, setItens] = useState([]);
  const [destinos, setDestinos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ultimo, setUltimo] = useState(null);   // { ids, texto } da última classificação (Desfazer dentro da janela)

  const carregar = useCallback(async () => {
    const r = await http.get(`/processos/${processoId}/fila`);
    setItens(r.data.itens || []);
    setDestinos(r.data.destinos || []);
    return r.data.itens || [];
  }, [processoId]);

  useEffect(() => { if (open) carregar().catch(() => toast.error("Não consegui abrir a fila")); else setUltimo(null); }, [open, carregar]);

  if (!total && !open) return null;

  const desfazer = async (ids) => {
    try {
      await http.post(`/processos/${processoId}/fila/desfazer`, { ids });
      setUltimo(null);
      if (open) await carregar();
      onChanged?.();
      toast.success("Devolvidos à fila");
    } catch { toast.error("Não consegui desfazer"); }
  };

  const classificar = async (ids, destino) => {
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await http.post(`/processos/${processoId}/fila/classificar`, { ids, destino });
      const feitos = r.data?.ids || [];
      const nome = destino === "descartar" ? null : (destinos.find((d) => d.processo_id === destino) || {});
      const texto = destino === "descartar" ? `${feitos.length} descartado(s)` : `${feitos.length} classificado(s) em ${autosLabel(nome.autos, nome.autos_nome)}`;
      const resto = await carregar();
      onChanged?.();
      if (!resto.length) {
        setOpen(false); setUltimo(null);
        toast.success(texto, { duration: 8000, action: { label: "Desfazer", onClick: () => desfazer(feitos) } });
      } else {
        setUltimo({ ids: feitos, texto });   // dentro da janela o toast não é clicável: o "Desfazer" fica aqui
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao classificar");
    } finally { setBusy(false); }
  };

  return (
    <>
      {total > 0 && (
        <div data-testid="fila-aviso" className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <Inbox className="w-4 h-4 shrink-0" strokeWidth={1.75} />
          <span className="mr-auto"><b>{total}</b> andamento{total === 1 ? "" : "s"} a classificar (de qual autos {total === 1 ? "é" : "são"}?)</span>
          <Button type="button" size="sm" data-testid="fila-abrir" onClick={() => setOpen(true)} className="bg-amber-700 text-white hover:bg-amber-800">Classificar ›</Button>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="fila-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">A classificar</DialogTitle>
            <DialogDescription>O app não soube de qual autos é cada andamento. Toque na etiqueta certa.</DialogDescription>
          </DialogHeader>
          {ultimo && (
            <div data-testid="fila-ultimo" className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
              <span className="mr-auto">{ultimo.texto}</span>
              <button type="button" data-testid="fila-desfazer" onClick={() => desfazer(ultimo.ids)} className="font-semibold underline underline-offset-2">Desfazer</button>
            </div>
          )}
          {itens.length > 1 && (
            <div className="rounded-md border border-stone-200 bg-stone-50 p-2" data-testid="fila-todos">
              <span className="text-xs font-semibold text-stone-600 mr-2">Todos para:</span>
              <span className="inline-flex flex-wrap gap-1.5 align-middle">
                {destinos.map((d) => (
                  <button key={d.processo_id} type="button" disabled={busy} data-testid={`fila-todos-${d.processo_id}`}
                    onClick={() => classificar(itens.map((i) => i.andamento_id), d.processo_id)}
                    className="min-h-[32px] rounded-full disabled:opacity-50"><AutosTag autos={d.autos} nome={d.autos_nome} /></button>
                ))}
              </span>
            </div>
          )}
          <ul className="space-y-3 max-h-[55vh] overflow-y-auto pr-1" data-testid="fila-lista">
            {itens.length === 0 && <li className="text-sm text-stone-500">Nada a classificar.</li>}
            {itens.map((i) => (
              <li key={i.andamento_id} data-testid={`fila-item-${i.andamento_id}`} className="rounded-md border border-stone-200 bg-white p-2.5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-stone-900 tabular-nums">{formatDate(i.data)}</span>
                  <span className="px-1.5 py-0.5 rounded border border-stone-300 text-[10px] font-bold uppercase tracking-wide text-stone-600">{ORIGEM[i.origem] || i.origem}</span>
                </div>
                <p className="text-xs text-stone-600 mt-1 leading-relaxed break-words">{i.descricao}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {destinos.map((d) => (
                    <button key={d.processo_id} type="button" disabled={busy} data-testid={`fila-${i.andamento_id}-${d.processo_id}`}
                      onClick={() => classificar([i.andamento_id], d.processo_id)}
                      className={`min-h-[36px] px-1 rounded-full disabled:opacity-50`}><AutosTag autos={d.autos} nome={d.autos_nome} className="py-1.5 px-2.5 text-[11px]" /></button>
                  ))}
                  <button type="button" disabled={busy} data-testid={`fila-descartar-${i.andamento_id}`} onClick={() => classificar([i.andamento_id], "descartar")}
                    className="min-h-[36px] px-3 text-xs font-semibold text-stone-500 hover:text-rose-brand underline underline-offset-2 ml-auto">Descartar</button>
                </div>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
